import { useEffect, useState } from "react";
import Breadcrumb from "../components/Breadcrumbs/Breadcrumb";
import CoverOne from "../images/cover/cover-01.png";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../app/store";
import { fetchUserMe, updateUserById } from "../app/userSlice";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Spin, Switch } from "antd";
import { Eye, EyeOff } from "lucide-react";

const Profile = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.user);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [editableData, setEditableData] = useState({
    first_name: "",
    last_name: "",
    mobile: "",
    profile_image: null as File | null | string,
    dob: "",
    gender: "",
    status: "active",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>("");

  useEffect(() => {
    const loadUser = async () => {
      try {
        const data = await dispatch(fetchUserMe()).unwrap();
        setEditableData((prev) => ({
          ...prev,
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          mobile: `${data.country_code}${data.mobile}`,
          profile_image: data.profile_image || "",
          dob: data.dob || "",
          gender: data.gender || "Male",
          status: data.status || "Active",
          email: data.email || "",
        }));
      } catch (err) {
        toast.error(err as string);
      } finally {
        setPageLoading(false);
      }
    };
    loadUser();
  }, [dispatch]);

  useEffect(() => {
    if (editableData.profile_image instanceof File) {
      const objectUrl = URL.createObjectURL(editableData.profile_image);
      setImagePreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (typeof editableData.profile_image === "string") {
      setImagePreviewUrl(editableData.profile_image);
    }
  }, [editableData.profile_image]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, files } = e.target as any;

    if (name === "profile_image" && files && files[0]) {
      const file = files[0];
      setEditableData((prev) => ({ ...prev, profile_image: file }));
      const previewURL = URL.createObjectURL(file);
      setImagePreviewUrl(previewURL);
    } else {
      setEditableData((prev) => ({
        ...prev,
        [name]: value.trimStart(),
      }));
    }
  };

  const handlePhoneChange = (value: string | undefined) => {
    setEditableData((prev) => ({ ...prev, mobile: value || "" }));
  };

  const handlePasswordToggle = (checked: boolean) => {
    setChangePassword(checked);
    if (!checked) {
      setEditableData((prev) => ({
        ...prev,
        password: "",
        confirmPassword: "",
      }));
    }
    setErrors({});
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!editableData.first_name.trim())
      newErrors.first_name = "First name is required";
    if (!editableData.last_name.trim())
      newErrors.last_name = "Last name is required";
    if (!editableData.mobile) newErrors.mobile = "Mobile number is required";
    else if (!/^\+91\d{10}$/.test(editableData.mobile))
      newErrors.mobile = "Mobile must be 10 digits with +91 prefix";
    if (!editableData.dob) newErrors.dob = "Date of Birth is required";
    // Password validation only if toggle enabled
    if (changePassword) {
      if (!editableData.password) newErrors.password = "Password is required";
      else if (editableData.password.length < 6)
        newErrors.password = "Password must be at least 6 characters";

      if (!editableData.confirmPassword)
        newErrors.confirmPassword = "Confirm Password is required";
      else if (editableData.password !== editableData.confirmPassword)
        newErrors.confirmPassword = "Passwords do not match";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!user || !validate()) return;
    setLoading(true);
    const form = new FormData();
    form.append("first_name", editableData.first_name);
    form.append("last_name", editableData.last_name);
    form.append("country_code", "+91");
    form.append("country_iso", "IN");

    const mobile = editableData.mobile?.startsWith("+91")
      ? editableData.mobile.replace("+91", "").trim()
      : "";
    form.append("mobile", mobile);
    form.append("dob", editableData.dob);
    form.append("gender", editableData.gender);
    form.append("status", editableData.status);
    form.append("email", editableData.email);
    if (changePassword) {
      form.append("password", editableData.password);
    }

    const fileInput = document.getElementById(
      "profile_image"
    ) as HTMLInputElement;
    const inputFile = fileInput?.files?.[0];

    if (inputFile) {
      form.append("profile_image", inputFile);
    } else if (editableData.profile_image instanceof File) {
      form.append("profile_image", editableData.profile_image);
    }

    try {
      await dispatch(updateUserById({ id: user.id, formData: form })).unwrap();
      toast.success("Profile Update Successfully");
      const updatedUser = await dispatch(fetchUserMe()).unwrap();
      localStorage.setItem("user", JSON.stringify(updatedUser));
      window.dispatchEvent(new Event("storage"));
      setIsEditing(false);
      setChangePassword(false);
    } catch (err: any) {
      toast.error(err?.message || err || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Show loader while page is loading
  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Breadcrumb pageName="Profile" />

      <div className="bg-white dark:bg-boxdark shadow rounded-md border border-stroke dark:border-strokedark">
        {/* Cover */}
        <div className="relative z-20 h-40 md:h-56">
          <img
            src={CoverOne}
            alt="cover"
            className="h-full w-full object-cover object-center rounded-tl-sm rounded-tr-sm"
          />
        </div>

        {/* Profile Image */}
        <div className="px-4 text-center">
          <div className="relative z-30 mx-auto -mt-20 h-32 w-32 sm:h-44 sm:w-44 rounded-full bg-white p-1 shadow-lg">
            <div className="relative w-full h-full">
              {editableData.profile_image || user?.profile_image ? (
                <img
                  src={
                    editableData.profile_image instanceof File
                      ? URL.createObjectURL(editableData.profile_image)
                      : editableData.profile_image || user?.profile_image
                  }
                  alt="profile"
                  className="rounded-full object-cover w-full h-full border-4 border-white"
                  onError={(e) => {
                    // hide broken img and fallback to initials
                    (e.target as HTMLImageElement).style.display = "none";
                    (
                      e.target as HTMLImageElement
                    ).parentElement!.innerHTML = `<span class="flex items-center justify-center w-full h-full rounded-full bg-gray-400 text-white font-semibold text-4xl">
             ${user?.name?.charAt(0).toUpperCase() || "U"}
           </span>`;
                  }}
                />
              ) : (
                <span className="flex items-center justify-center w-full h-full rounded-full bg-gray-400 text-white font-semibold text-4xl">
                  {user?.name?.charAt(0).toUpperCase() || "U"}
                </span>
              )}

              {isEditing && (
                <label
                  htmlFor="profile_image"
                  className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 flex h-8 w-8 items-center justify-center bg-primary text-white rounded-full cursor-pointer shadow-md"
                >
                  ✎
                  <input
                    type="file"
                    name="profile_image"
                    id="profile_image"
                    className="sr-only"
                    accept="image/*"
                    onChange={handleChange}
                  />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Profile Info */}
        <div className="text-center px-6 py-6">
          <h2 className="text-2xl font-semibold text-black dark:text-white">
            {user?.first_name} {user?.last_name}
          </h2>
          <p className="text-gray-500">{user?.email}</p>
        </div>

        {/* Editable Form */}
        <div className="px-6 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4  from-white to-gray-100 dark:from-boxdark dark:to-gray-900 border border-stroke dark:border-strokedark rounded-lg p-6">
            {/* First Name */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-white">
                First Name
              </label>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    name="first_name"
                    value={editableData.first_name}
                    onChange={handleChange}
                    maxLength={50}
                    className={`w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                      errors.first_name ? "border-red-500" : "border-stroke"
                    }`}
                  />
                  {errors.first_name && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.first_name}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-black dark:text-white">
                  {user?.first_name}
                </p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Last Name
              </label>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    name="last_name"
                    value={editableData.last_name}
                    onChange={handleChange}
                    maxLength={50}
                    className={`w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                      errors.last_name ? "border-red-500" : "border-stroke"
                    }`}
                  />
                  {errors.last_name && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.last_name}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-black dark:text-white">
                  {user?.last_name}
                </p>
              )}
            </div>

            {/* PhoneInput */}
            <div className="relative z-50">
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Mobile Number
              </label>
              {isEditing ? (
                <>
                  <PhoneInput
                    defaultCountry="IN"
                    value={editableData.mobile}
                    onChange={handlePhoneChange}
                    international
                    countryCallingCodeEditable={false}
                    countrySelectProps={{ style: { display: "none" } }}
                    className={`!w-full !rounded-md !border  !px-4  !text-black dark:!bg-form-input dark:!text-white ${
                      errors.mobile ? "border-red-500" : "border-stroke"
                    }`}
                    onKeyDownCapture={(
                      e: React.KeyboardEvent<HTMLInputElement>
                    ) => {
                      const key = e.key;
                      const isDigit = /^[0-9]$/.test(key);
                      const allowedKeys = [
                        "Backspace",
                        "ArrowLeft",
                        "ArrowRight",
                        "Delete",
                        "Tab",
                      ];
                      const digitsOnly = editableData.mobile.replace(/\D/g, "");

                      if (!isDigit && !allowedKeys.includes(key)) {
                        e.preventDefault();
                      }

                      if (isDigit && digitsOnly.length >= 12) {
                        e.preventDefault();
                      }
                    }}
                  />
                  {errors.mobile && (
                    <p className="text-red-500 text-sm mt-1">{errors.mobile}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-black dark:text-white">
                  {user?.country_code + " " + user?.mobile}
                </p>
              )}
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Date of Birth
              </label>
              {isEditing ? (
                <>
                  <input
                    type="date"
                    name="dob"
                    value={editableData.dob}
                    onChange={handleChange}
                    max={new Date().toISOString().split("T")[0]}
                    className={`w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                      errors.dob ? "border-red-500" : "border-stroke"
                    }`}
                  />
                  {errors.dob && (
                    <p className="text-red-500 text-sm mt-1">{errors.dob}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-black dark:text-white">
                  {user?.dob
                    ? new Date(user?.dob).toLocaleDateString("en-GB")
                    : "--"}
                </p>
              )}
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Gender
              </label>
              {isEditing ? (
                <div className="flex gap-4">
                  {["Male", "Female", "Prefer not to say"].map((option) => {
                    const value =
                      option === "Prefer not to say"
                        ? "other"
                        : option.toLowerCase();

                    return (
                      <label
                        key={option}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="radio"
                          name="gender"
                          value={value}
                          checked={editableData.gender === value}
                          onChange={handleChange}
                          className="text-black dark:text-white"
                        />
                        {option}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-black dark:text-white">
                  {editableData.gender}
                </p>
              )}
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Status
              </label>
              {isEditing ? (
                <select
                  name="status"
                  value={editableData.status}
                  onChange={handleChange}
                  className="w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              ) : (
                <p className="text-sm text-black dark:text-white">
                  {editableData.status}
                </p>
              )}
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-white">
                Email
              </label>
              {isEditing ? (
                <>
                  <input
                    type="email"
                    name="email"
                    value={editableData.email}
                    onChange={handleChange}
                    className={`w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                      errors.email ? "border-red-500" : "border-stroke"
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-black dark:text-white">
                  {editableData.email}
                </p>
              )}
            </div>
            {/* ⬇️ PASSWORD TOGGLE */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <span className="font-medium text-gray-700 dark:text-white">
                  Would You Like To Change Password?
                </span>
                <Switch
                  checked={changePassword}
                  onChange={handlePasswordToggle}
                  disabled={!isEditing || loading}
                />
              </div>
            </div>

            {/* PASSWORD FIELDS */}
            {changePassword && (
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* PASSWORD */}
                <div>
                  <label className="block mb-2 text-sm font-medium">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={editableData.password}
                      onChange={handleChange}
                      className="w-full rounded-lg border py-2 px-4"
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-3 flex items-center"
                    >
                      {!showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-sm">{errors.password}</p>
                  )}
                </div>

                {/* CONFIRM PASSWORD */}
                <div>
                  <label className="block mb-2 text-sm font-medium">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={editableData.confirmPassword}
                      onChange={handleChange}
                      className="w-full rounded-lg border py-2 px-4"
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute inset-y-0 right-3 flex items-center"
                    >
                      {!showConfirmPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 mt-6">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-black text-white px-6 py-2 rounded-md hover:bg-opacity-80 transition"
                >
                  {loading ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="bg-gray-300 text-black px-6 py-2 rounded-md hover:bg-opacity-80 transition"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="bg-black text-white px-6 py-2 rounded-md hover:bg-opacity-90"
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;
